/*
  MTT Holo-Sticker (24.10.2026, Johns Wunsch aus der TikTok-Durchsicht).
  Webkomponente <mtt-holo text="Limitiert" muster="bursts">, ohne React, für das Liquid-Theme.
  Shader und Bewegungslogik portiert aus „HoloCard“ von React Bits (github.com/DavidHDev/react-bits):

  MIT + Commons Clause License Condition v1.0 · Copyright (c) 2026 David Haz
  Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
  documentation files (the "Software"), to deal in the Software without restriction, including without limitation
  the rights to use, copy, modify, merge, publish, and distribute the Software as part of an application, website,
  or product, subject to the following conditions: The above copyright notice and this permission notice shall be
  included in all copies or substantial portions of the Software. Commons Clause: You may use this Software,
  including for any commercial purpose, so long as you do not sell, sublicense, or redistribute the components
  themselves, whether alone, in a bundle, or as a ported version.

  Eigene Ergänzungen: Sticker-Motiv per Canvas (kein Bild nötig), Grenze von 8 WebGL-Flächen je Seite
  (danach CSS-Schimmer), Bewegung über den ganzen Kartenbereich, prefers-reduced-motion ohne Bewegung.
*/
const VERTEX = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAGMENT = `#version 300 es
precision highp float;

uniform sampler2D uArt;
uniform vec2 uSize;
uniform float uAspect;
uniform vec2 uTilt;
uniform vec2 uLight;
uniform float uDistance;
uniform int uPreset;
uniform float uIntensity;
uniform float uScale;
uniform float uEdge;
uniform float uFrame;
uniform float uRadius;
uniform float uGlare;
uniform vec3 uFoil;
uniform float uReady;

out vec4 outColor;

const float TAU = 6.28318530718;

struct Foil {
  float mask;
  float angle;
  float jitter;
  vec2 facet;
};

float hash(vec2 p) {
  p = fract(p * vec2(234.34, 435.345));
  p += dot(p, p + 34.23);
  return fract(p.x * p.y);
}

vec2 hash2(vec2 p) {
  return vec2(hash(p), hash(p + 17.31));
}

vec3 hue(float t) {
  t = abs(fract(t * 0.5) * 2.0 - 1.0);
  vec3 c = mix(vec3(0.1, 0.25, 1.0), vec3(0.0, 0.85, 1.0), smoothstep(0.0, 0.25, t));
  c = mix(c, vec3(0.25, 1.0, 0.2), smoothstep(0.25, 0.45, t));
  c = mix(c, vec3(1.0, 0.92, 0.05), smoothstep(0.45, 0.65, t));
  c = mix(c, vec3(1.0, 0.45, 0.05), smoothstep(0.65, 0.85, t));
  return mix(c, vec3(0.95, 0.12, 0.3), smoothstep(0.85, 1.0, t));
}

Foil bursts(vec2 p) {
  vec2 q = mat2(0.7071068, 0.7071068, -0.7071068, 0.7071068) * (p - vec2(0.5, 0.5 * uAspect)) / (0.283 / uScale);
  vec2 d = fract(q) - 0.5;
  float u = length(d) / 0.49;
  float angle = atan(d.y, d.x);
  if (u < 0.12) {
    float ring = min(floor(u / 0.045 + 0.5), 2.0);
    float count = max(1.0, ring * 6.0);
    float spot = (floor(angle / TAU * count) + 0.5) / count * TAU;
    vec2 dot = vec2(cos(spot), sin(spot)) * ring * 0.045;
    float m = smoothstep(0.024, 0.012, length(d / 0.49 - dot));
    return Foil(m, angle, 0.5, vec2(0.0));
  }
  bool inner = u < 0.66;
  float slot = angle / TAU * (inner ? 56.0 : 72.0);
  bool shifted = mod(floor(slot), 2.0) > 0.5;
  vec2 band = inner ? (shifted ? vec2(0.45, 0.61) : vec2(0.33, 0.49)) : (shifted ? vec2(0.84, 0.99) : vec2(0.7, 0.85));
  float across = abs(u - (band.x + band.y) * 0.5) / ((band.y - band.x) * 0.5);
  float along = abs(fract(slot) - 0.5) * 2.0;
  float width = inner ? 0.72 : 0.6;
  float m = smoothstep(1.0, 0.78, across) * smoothstep(width, width - 0.2, along);
  return Foil(m, angle, 0.5, vec2(0.0));
}

Foil stars(vec2 p) {
  Foil best = Foil(0.0, 0.0, 0.0, vec2(0.0));
  vec2 g = p / (0.08 / uScale);
  vec2 base = floor(g);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 id = base + vec2(float(i), float(j));
      float pick = hash(id + 1.3);
      vec2 d = g - (id + 0.5 + (hash2(id) - 0.5) * 0.55);
      float m = smoothstep(0.07, 0.035, length(d));
      if (pick < 0.62) {
        float size = mix(0.16, 0.5, pow(hash(id + 9.0), 1.8));
        float sector = TAU / (pick < 0.3 ? 4.0 : 5.0);
        float k = abs(fract((atan(d.y, d.x) + hash(id + 4.0) * TAU) / sector) - 0.5) * 2.0;
        float edge = size * mix(1.0, 0.36, pow(k, 0.6));
        m = smoothstep(edge, edge * 0.8, length(d));
      }
      if (m > best.mask) best = Foil(m, hash(id + 6.0) * TAU, hash(id + 8.0), (hash2(id + 2.0) - 0.5) * 0.6);
    }
  }
  return best;
}

Foil shards(vec2 p) {
  vec2 g = p * 19.0 * uScale;
  vec2 base = floor(g);
  float best = 9.0;
  float second = 9.0;
  vec2 id = base;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 cell = base + vec2(float(i), float(j));
      float d = length(cell + 0.08 + hash2(cell) * 0.84 - g);
      if (d < best) {
        second = best;
        best = d;
        id = cell;
      } else if (d < second) {
        second = d;
      }
    }
  }
  float crack = smoothstep(0.015, 0.07, second - best);
  float shine = 0.25 + 0.75 * pow(hash(id + 7.7), 2.0);
  return Foil((0.2 + 0.8 * crack) * shine, hash(id + 3.17) * TAU, hash(id + 11.73), (hash2(id + 5.0) - 0.5) * 0.5);
}

Foil cosmos(vec2 p) {
  Foil best = Foil(0.0, 0.0, 0.0, vec2(0.0));
  vec2 g = p / (0.16 / uScale);
  vec2 base = floor(g);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 id = base + vec2(float(i), float(j));
      vec2 d = g - (id + 0.5 + (hash2(id) - 0.5) * 0.7);
      float size = mix(0.16, 0.52, hash(id + 2.0));
      float r = length(d);
      float m = max(smoothstep(0.05, 0.0, abs(r - size)), smoothstep(size, size - 0.04, r) * 0.22);
      if (m > best.mask) best = Foil(m, hash(id + 6.0) * TAU + atan(d.y, d.x) * 0.5, hash(id + 1.0), (hash2(id + 4.0) - 0.5) * 0.5);
    }
  }
  vec2 fine = p / (0.035 / uScale);
  vec2 cell = floor(fine);
  float dots = smoothstep(0.2, 0.1, length(fine - cell - 0.5 - (hash2(cell + 9.0) - 0.5) * 0.6)) * step(0.55, hash(cell + 2.0));
  if (dots > best.mask) best = Foil(dots, hash(cell) * TAU, hash(cell + 3.0), (hash2(cell + 7.0) - 0.5) * 0.6);
  return best;
}

Foil rainbow(vec2 p) {
  return Foil(0.86 + 0.14 * hash(floor(p * 700.0 * uScale)), 0.2, 0.5, vec2(0.0));
}

Foil swirl(vec2 p) {
  vec2 d = p - vec2(0.5, 0.42 * uAspect);
  float angle = atan(d.y, d.x);
  float rays = 0.55 + 0.45 * smoothstep(0.3, 0.7, abs(fract(angle / TAU * 60.0 * uScale) - 0.5) * 2.0);
  return Foil(rays, angle, length(d), vec2(0.0));
}

Foil glitter(vec2 p) {
  vec2 g = p * 72.0 * uScale;
  vec2 cell = floor(g);
  float size = mix(0.18, 0.4, hash(cell + 3.0));
  float m = smoothstep(size, size * 0.55, length(fract(g) - 0.5 - (hash2(cell) - 0.5) * 0.5)) * step(0.25, hash(cell + 5.0));
  return Foil(m, hash(cell + 7.0) * TAU, hash(cell + 9.0), (hash2(cell + 11.0) - 0.5) * 0.9);
}

Foil gold(vec2 p) {
  Foil flake = glitter(p * 0.8);
  if (flake.mask > 0.5) return flake;
  float etch = 0.55 + 0.45 * smoothstep(0.2, 0.8, abs(fract((p.x * 0.6 + p.y) * 140.0 * uScale) - 0.5) * 2.0);
  return Foil(etch, 0.9, 0.4, vec2(0.0));
}

vec3 sparkles(vec2 p, vec3 halfway, vec2 sweep, float awake) {
  vec2 g = p * 62.0;
  vec2 cell = floor(g);
  vec2 d = fract(g) - 0.5 - (hash2(cell) - 0.5) * 0.36;
  float spin = hash(cell + 3.0) * TAU;
  d = mat2(cos(spin), -sin(spin), sin(spin), cos(spin)) * d;
  vec2 a = abs(d);
  float hexagon = max(a.x * 0.866 + a.y * 0.5, a.y);
  float size = mix(0.22, 0.38, hash(cell + 2.0));
  float piece = smoothstep(size, size - 0.07, hexagon) * step(0.42, hash(cell + 4.0));
  vec3 facet = normalize(vec3((hash2(cell + 6.0) - 0.5) * 0.9, 1.0));
  float flash = pow(max(dot(facet, halfway), 0.0), 6.0);
  vec3 tint = hue(dot(sweep, vec2(cos(spin), sin(spin))) * 1.2 + dot(sweep, vec2(0.8, 0.6)) * 0.8 + hash(cell + 8.0) * 0.5);
  vec3 color = piece * tint * (0.3 + 1.6 * flash) * awake;
  vec2 fine = p * 210.0;
  vec2 speckCell = floor(fine);
  float speck = smoothstep(0.24, 0.0, length(fract(fine) - 0.5 - (hash2(speckCell) - 0.5) * 0.5)) * step(0.72, hash(speckCell + 1.0));
  float speckFlash = pow(max(dot(normalize(vec3((hash2(speckCell + 2.0) - 0.5) * 0.9, 1.0)), halfway), 0.0), 10.0);
  return color + vec3(1.2) * speck * speckFlash * (1.0 - piece);
}

void main() {
  vec2 uv = vec2(gl_FragCoord.x / uSize.x, 1.0 - gl_FragCoord.y / uSize.y);
  vec2 p = vec2(uv.x, uv.y * uAspect);
  vec2 center = vec2(0.5, 0.5 * uAspect);
  vec2 q = abs(p - center) - center + uRadius;
  float outside = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - uRadius;
  float pixel = 1.0 / uSize.x;
  float alpha = clamp(0.5 - outside / pixel, 0.0, 1.0);
  vec4 sampled = texture(uArt, uv);
  if (uReady > 0.5) alpha *= sampled.a;
  if (alpha <= 0.0) {
    outColor = vec4(0.0);
    return;
  }
  vec3 art = uReady > 0.5 ? pow(sampled.rgb, vec3(2.2)) : vec3(0.12);

  mat3 rx = mat3(1.0, 0.0, 0.0, 0.0, cos(uTilt.x), sin(uTilt.x), 0.0, -sin(uTilt.x), cos(uTilt.x));
  mat3 ry = mat3(cos(uTilt.y), 0.0, -sin(uTilt.y), 0.0, 1.0, 0.0, sin(uTilt.y), 0.0, cos(uTilt.y));
  mat3 turn = rx * ry;
  vec3 world = turn * vec3(p - center, 0.0);
  vec3 view = normalize(transpose(turn) * (vec3(0.0, 0.0, uDistance) - world));
  vec3 lamp = vec3((uLight - 0.5) * vec2(1.1, 1.1 * uAspect), 0.9);
  vec3 light = normalize(transpose(turn) * (lamp - world));
  vec3 halfway = normalize(light + view);
  vec2 sweep = light.xy + view.xy;
  float sheen = pow(max(halfway.z, 0.0), 10.0);
  float shine = pow(max(halfway.z, 0.0), 80.0);
  float frame = 1.0 - smoothstep(uFrame - pixel, uFrame + pixel, -outside);

  Foil f;
  if (uPreset == 0) f = bursts(p);
  else if (uPreset == 1) f = stars(p);
  else if (uPreset == 2) f = shards(p);
  else if (uPreset == 3) f = cosmos(p);
  else if (uPreset == 4) f = rainbow(p);
  else if (uPreset == 5) f = swirl(p);
  else if (uPreset == 6) f = glitter(p);
  else f = gold(p);

  vec3 metal = uPreset == 7 ? vec3(1.0, 0.74, 0.38) * dot(uFoil, vec3(0.3333)) * 1.15 : uFoil;
  vec4 look = uPreset == 0 ? vec4(0.25, 1.9, 0.12, 1.0)
    : uPreset == 1 ? vec4(0.6, 1.4, 0.3, 1.0)
    : uPreset == 2 ? vec4(0.9, 0.9, 0.6, 0.5)
    : uPreset == 3 ? vec4(0.7, 1.2, 0.35, 0.85)
    : uPreset == 4 ? vec4(0.0, 2.2, 0.0, 0.45)
    : uPreset == 5 ? vec4(1.6, 0.3, 0.0, 0.4)
    : uPreset == 6 ? vec4(0.9, 1.0, 0.6, 1.0)
    : vec4(0.3, 1.0, 0.2, 0.95);
  vec2 dir = vec2(cos(f.angle), sin(f.angle));
  float phase = dot(sweep, dir) * look.x + dot(sweep, vec2(0.8, 0.6)) * look.y + f.jitter * look.z;
  float spoke = 1.0;
  if (uPreset == 5) {
    float facing = dot(normalize(sweep + 0.0001), dir);
    phase = f.jitter * 2.4 + facing * 0.6 + length(sweep) * 0.8;
    spoke = 0.3 + 0.7 * smoothstep(0.25, 0.95, abs(facing));
  }
  vec3 tint = hue(phase);
  if (uPreset == 7) tint = mix(tint, metal, 0.65);
  float glint = pow(max(dot(normalize(vec3(f.facet, 1.0)), halfway), 0.0), 24.0) * step(0.001, length(f.facet));
  bool layered = uPreset == 2 || uPreset == 4 || uPreset == 5 || uPreset == 7;
  vec3 foil = (layered ? tint : mix(tint, vec3(1.0), 0.12)) * (1.15 + 0.3 * sheen) + metal * glint * 1.2;
  float tilted = smoothstep(0.02, 0.21, length(sin(uTilt)));
  float awake = mix(0.2, 1.0, tilted);
  float strength = uIntensity * f.mask * (1.0 - frame) * awake;
  float band = uPreset == 5 ? spoke : layered ? 0.25 + 0.75 * smoothstep(0.3, 0.95, 0.5 + 0.5 * cos(phase * TAU * 0.5 + 1.3)) : 1.0;
  vec3 color = layered
    ? 1.0 - (1.0 - art) * (1.0 - clamp(foil * look.w * band * strength, 0.0, 1.0))
    : mix(art, foil, clamp(strength * look.w, 0.0, 1.0));
  color += sparkles(p, halfway, sweep, mix(0.55, 1.0, tilted)) * uEdge * frame;
  color += uFoil * sheen * 0.12 * frame;
  color += vec3(sheen * 0.07 + shine * 0.3) * uGlare;
  color = pow(clamp(color, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(color * alpha, alpha);
}
`;

const PRESETS = ['bursts', 'stars', 'shards', 'cosmos', 'rainbow', 'swirl', 'glitter', 'gold'];
const PERSPECTIVE = 1100;
const MAX_GL = 8;
let glZahl = 0;

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

const parseColor = value => {
  const fallback = [0.78, 0.8, 0.84];
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return fallback;
  ctx.fillStyle = '#000000';
  ctx.fillStyle = value;
  const r = ctx.fillStyle;
  if (!r.startsWith('#') || r.length !== 7) return fallback;
  return [1, 3, 5].map(i => Math.pow(parseInt(r.slice(i, i + 2), 16) / 255, 2.2));
};

const compile = (gl, type, source) => {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, source);
  gl.compileShader(sh);
  if (gl.getShaderParameter(sh, gl.COMPILE_STATUS)) return sh;
  gl.deleteShader(sh);
  return null;
};

/* Sticker-Motiv: dunkle Graphit-Fläche, feiner Rand, Text in Versalien (eine oder zwei Zeilen). */
function motiv(text, px) {
  const c = document.createElement('canvas');
  c.width = c.height = px;
  const g = c.getContext('2d');
  const r = px * 0.2;
  const weg = () => {
    g.beginPath();
    g.moveTo(r, 0); g.arcTo(px, 0, px, px, r); g.arcTo(px, px, 0, px, r); g.arcTo(0, px, 0, 0, r); g.arcTo(0, 0, px, 0, r);
    g.closePath();
  };
  const v = g.createLinearGradient(0, 0, px, px);
  v.addColorStop(0, '#3a3f4a');
  v.addColorStop(0.5, '#1c1f26');
  v.addColorStop(1, '#2c3039');
  weg(); g.fillStyle = v; g.fill();
  g.lineWidth = px * 0.035; g.strokeStyle = 'rgba(255,255,255,0.55)';
  g.save(); g.translate(px * 0.06, px * 0.06); g.scale(0.88, 0.88); weg(); g.stroke(); g.restore();
  const worte = String(text || '').trim().toUpperCase().split(/\s+/).filter(Boolean);
  let zeilen = worte.length > 1 ? [worte.slice(0, Math.ceil(worte.length / 2)).join(' '), worte.slice(Math.ceil(worte.length / 2)).join(' ')] : worte;
  if (!zeilen.length) zeilen = ['MTT'];
  g.fillStyle = '#ffffff'; g.textAlign = 'center'; g.textBaseline = 'middle';
  const breit = px * 0.8;
  let gr = px * 0.3;
  const schrift = s => `800 ${s}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
  for (const z of zeilen) { g.font = schrift(gr); while (g.measureText(z).width > breit && gr > 8) { gr -= 1; g.font = schrift(gr); } }
  g.font = schrift(gr);
  const hoehe = gr * 1.08;
  const start = px / 2 - (hoehe * (zeilen.length - 1)) / 2;
  zeilen.forEach((z, i) => g.fillText(z, px / 2, start + i * hoehe));
  return c;
}

class MttHolo extends HTMLElement {
  connectedCallback() {
    if (this._an) return;
    this._an = true;
    const s = {
      preset: Math.max(0, PRESETS.indexOf(this.getAttribute('muster') || 'bursts')),
      intensity: 0.85, scale: Number(this.getAttribute('skala') || 1), edgeSparkle: 0.45, frame: 0.035, glare: 0.5,
      foil: parseColor(this.getAttribute('folie') || '#e2e6ec'), radius: 0.2, tiltMax: 16, hoverScale: 1.06, idle: true
    };
    this.innerHTML = '<span class="mtt-holo__rotor"><canvas></canvas></span>';
    const rotor = this.firstElementChild;
    const canvas = rotor.firstElementChild;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const gl = glZahl < MAX_GL ? canvas.getContext('webgl2', {alpha: true, premultipliedAlpha: true, antialias: false}) : null;
    let program = null, texture = null;
    const u = {};
    if (gl) {
      const vs = compile(gl, gl.VERTEX_SHADER, VERTEX), fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
      if (vs && fs) {
        program = gl.createProgram();
        gl.attachShader(program, vs); gl.attachShader(program, fs);
        gl.bindAttribLocation(program, 0, 'position');
        gl.linkProgram(program);
        gl.deleteShader(vs); gl.deleteShader(fs);
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) { gl.deleteProgram(program); program = null; }
      }
      if (program) {
        glZahl++;
        const buf = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
        texture = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, motiv(this.getAttribute('text'), 512));
        gl.generateMipmap(gl.TEXTURE_2D);
        for (const n of ['uArt', 'uSize', 'uAspect', 'uTilt', 'uLight', 'uDistance', 'uPreset', 'uIntensity', 'uScale', 'uEdge', 'uFrame', 'uRadius', 'uGlare', 'uFoil', 'uReady']) u[n] = gl.getUniformLocation(program, n);
      }
    }
    if (!program) { /* Ohne WebGL: Motiv als Bild mit CSS-Schimmer */
      this.classList.add('mtt-holo--css');
      const bild = motiv(this.getAttribute('text'), 256);
      canvas.replaceWith(bild);
    }
    const st = {tiltX: 0, tiltY: 0, tiltVX: 0, tiltVY: 0, lightX: 0.36, lightY: 0.26, lightVX: 0, lightVY: 0, lift: 1, liftV: 0, clock: Math.random() * 40};
    const zeiger = {x: 0.5, y: 0.5, inside: false};
    let raf = 0, last = 0, visible = true, calm = 0;
    const draw = () => {
      if (!program) return;
      const w = canvas.width, h = canvas.height;
      if (!w || !h) return;
      gl.viewport(0, 0, w, h); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(program);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(u.uArt, 0); gl.uniform2f(u.uSize, w, h); gl.uniform1f(u.uAspect, h / w);
      gl.uniform2f(u.uTilt, (st.tiltX * Math.PI) / 180, (st.tiltY * Math.PI) / 180);
      gl.uniform2f(u.uLight, st.lightX, st.lightY);
      gl.uniform1f(u.uDistance, PERSPECTIVE / Math.max(1, canvas.clientWidth));
      gl.uniform1i(u.uPreset, s.preset); gl.uniform1f(u.uIntensity, s.intensity); gl.uniform1f(u.uScale, s.scale);
      gl.uniform1f(u.uEdge, s.edgeSparkle); gl.uniform1f(u.uFrame, s.frame); gl.uniform1f(u.uRadius, s.radius);
      gl.uniform1f(u.uGlare, s.glare); gl.uniform3f(u.uFoil, s.foil[0], s.foil[1], s.foil[2]); gl.uniform1f(u.uReady, 1);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    const spring = (v, vel, ziel, k, d, dt) => { const n = vel + ((ziel - v) * k - vel * d) * dt; return [v + n * dt, n]; };
    const tick = now => {
      raf = 0;
      if (!this.isConnected) return;
      const dt = Math.min(1 / 30, Math.max(1 / 240, (now - last) / 1000));
      last = now;
      let tx = 0, ty = 0, lx = 0.36, ly = 0.26, lift = 1;
      const drift = s.idle && !reduce && !zeiger.inside;
      if (zeiger.inside && !reduce) {
        tx = (0.5 - zeiger.y) * 2 * s.tiltMax; ty = (zeiger.x - 0.5) * 2 * s.tiltMax; lx = zeiger.x; ly = zeiger.y; lift = s.hoverScale;
      } else if (drift) {
        st.clock += dt; const t = st.clock;
        tx = Math.sin(t * 0.7) * s.tiltMax * 0.28; ty = Math.sin(t * 0.53 + 1.2) * s.tiltMax * 0.4;
        lx = 0.5 + Math.sin(t * 0.41) * 0.42; ly = 0.38 + Math.sin(t * 0.33 + 0.6) * 0.3;
      }
      const schritte = Math.ceil(dt / (1 / 240)), h = dt / schritte;
      for (let i = 0; i < schritte; i++) {
        [st.tiltX, st.tiltVX] = spring(st.tiltX, st.tiltVX, tx, 150, 16, h);
        [st.tiltY, st.tiltVY] = spring(st.tiltY, st.tiltVY, ty, 150, 16, h);
        [st.lightX, st.lightVX] = spring(st.lightX, st.lightVX, lx, 220, 30, h);
        [st.lightY, st.lightVY] = spring(st.lightY, st.lightVY, ly, 220, 30, h);
        [st.lift, st.liftV] = spring(st.lift, st.liftV, lift, 320, 30, h);
      }
      rotor.style.transform = `perspective(${PERSPECTIVE}px) scale(${st.lift}) rotateX(${st.tiltX}deg) rotateY(${st.tiltY}deg)`;
      draw();
      const bew = Math.abs(st.tiltVX) + Math.abs(st.tiltVY) + Math.abs(st.liftV) * 40 + (Math.abs(st.lightVX) + Math.abs(st.lightVY)) * 60 + Math.abs(st.tiltX - tx) + Math.abs(st.tiltY - ty);
      calm = bew > 0.02 ? 0 : calm + dt;
      if (!visible || (!drift && !zeiger.inside && calm > 0.3)) return;
      raf = requestAnimationFrame(tick);
    };
    const wake = () => { if (raf || !this.isConnected) return; calm = 0; last = performance.now(); raf = requestAnimationFrame(tick); };
    const resize = () => {
      if (!program) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr)), h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
      draw(); wake();
    };
    /* Bewegung über die ganze Karte bzw. das ganze Produktbild, nicht nur über dem kleinen Sticker */
    const flaeche = this.closest('product-card, .mtt-karte, [data-mtt-holo-flaeche]') || this.parentElement || this;
    const folgen = e => {
      if (e.pointerType === 'touch') return;
      const r = flaeche.getBoundingClientRect();
      zeiger.x = clamp((e.clientX - r.left) / r.width, 0, 1); zeiger.y = clamp((e.clientY - r.top) / r.height, 0, 1); zeiger.inside = true;
      wake();
    };
    flaeche.addEventListener('pointermove', folgen, {passive: true});
    flaeche.addEventListener('pointerenter', folgen, {passive: true});
    flaeche.addEventListener('pointerleave', () => { zeiger.inside = false; wake(); }, {passive: true});
    new ResizeObserver(resize).observe(this);
    new IntersectionObserver(es => { visible = es.some(x => x.isIntersecting); if (visible) wake(); }).observe(this);
    resize();
  }
}

if (!customElements.get('mtt-holo')) {
  const css = document.createElement('style');
  css.textContent = `
    mtt-holo[hidden] { display: none !important; }
    mtt-holo { position: absolute; z-index: 3; display: block; width: var(--mtt-holo-gr, 72px); aspect-ratio: 1; pointer-events: none;
      top: var(--mtt-holo-oben, 8px); right: var(--mtt-holo-rechts, 8px); rotate: var(--mtt-holo-dreh, 8deg);
      filter: drop-shadow(0 6px 10px rgba(0,0,0,.35)); }
    mtt-holo .mtt-holo__rotor { position: absolute; inset: 0; display: block; will-change: transform; border-radius: 20%; overflow: hidden; }
    mtt-holo canvas { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }
    mtt-holo.mtt-holo--css .mtt-holo__rotor::after { content: ""; position: absolute; inset: 0; mix-blend-mode: color-dodge; opacity: .55;
      background: linear-gradient(115deg, transparent 20%, #ff7ad9 35%, #7af0ff 50%, #ffe27a 65%, transparent 80%); background-size: 250% 250%;
      animation: mtt-holo-css 4s linear infinite; }
    @keyframes mtt-holo-css { from { background-position: 100% 0; } to { background-position: -150% 0; } }
    @media (prefers-reduced-motion: reduce) { mtt-holo.mtt-holo--css .mtt-holo__rotor::after { animation: none; } }
    @media (max-width: 749px) { mtt-holo { width: calc(var(--mtt-holo-gr, 72px) * .8); } }
    /* 01.11.2026 (John): in Tablet-Breite deckte der Sticker die Produktbilder ab, höchstens 16 % der Bildbreite */
    @media (min-width: 750px) and (max-width: 1199px) { mtt-holo { width: min(var(--mtt-holo-gr, 72px), 16%); } }
  `;
  document.head.appendChild(css);
  customElements.define('mtt-holo', MttHolo);
}
